import React from "react";
import { Routes, Route } from "react-router-dom";

// css
import styles from "./Mypage.module.scss";

import MyPageTop from "./MyPageTop";
import DatabaseEntrySettingsContainer from "./user_settings/database_entries/DatabaseEntrySettingsContainer";
import CreateDatabaseEntryContainer from "./user_settings/database_entries/CreateDatabaseEntryContainer";
import EditDatabaseEntryContainer from "./user_settings/database_entries/EditDatabaseEntryContainer";
import TeamOwnerSettingsContainer from "./user_settings/teams/TeamOwnerSettingsContainer";
import CreateTeamContainer from "./user_settings/teams/CreateTeamContainer";
import CreateProjectContainer from "./user_settings/projects/CreateProjectContainer";
import EditProjectContainer from "./user_settings/projects/EditProjectContainer";
import ProjectSettingsContainer from "./user_settings/projects/ProjectSettingsContainer";
import OtherSettingsPage from "./user_settings/other/OtherSettingsPage";
import EditTeamContainer from "./user_settings/teams/EditTeamContainer";
import TeamMembersContainer from "./user_settings/teams/TeamMembersContainer";
import EventSettingsContainer from "./user_settings/events/EventSettingsContainer";
import EditEventContainer from "./user_settings/events/EditEventContainer";
import CreateEventContainer from "./user_settings/events/CreateEventContainer";
import ArchiveMypageProject from "./user_settings/projects/ArchiveMypageProject";
import ChatListContainer from "../chat/ChatListContainer";
import ChatPageContainer from "../chat/ChatPageContainer";
import PageTitle from "../../common/PageTitle";
import RequireAuth from "../../common/routing/RequireAuth";
import MyPageSideBar from "../../features/mypage/sidebar/MyPageSideBar";
import { IconEsportsPersonSVG } from "../../assets/img/iconEsportsPersonSVG";
import { IconGearSVG } from "../../assets/img/IconGearSVG";
import { IconProjectSVG } from "../../assets/img/IconProjectSVG";
import { IconTeamOwnerSVG } from "../../assets/img/IconTeamOwnerSVG";
import { IconCalendarSVG } from "../../assets/img/iconCalendarSVG";
import TopicSettingsContainer from "./user_settings/topics/TopicSettingsContainer";
import { IconTopicsSVG } from "../../assets/img/IconTopicsSVG";
import { IconFacilitySVG } from "../../assets/img/facility/IconFacilitySVG";
import { IconChatSVG } from "../../assets/img/IconChatSVG";
import CreateTopicContainer from "./user_settings/topics/CreateTopicContainer";
import EditTopicContainer from "./user_settings/topics/EditTopicContainer";
import FacilitySettingContainer from "./user_settings/facility/FacilitySettingContainer";
import CreateFacilityContainer from "./user_settings/facility/CreateFacilityContainer";
import EditFacilityContainer from "./user_settings/facility/EditFacilityContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import OfferHistoryContainer from "./offer/OfferHistoryContainer";
import { useMetaData } from "../../utils/useMetaData";

type Props = {
  roles?: string[];
  logoutHandler: () => void;
};

const MyPage: React.FC<Props> = ({ roles, logoutHandler }) => {
  const settings: Array<{
    link: string;
    title: string;
    icon: any;
    description: string;
  }> = [
    {
      link: "/mypage/settings",
      title: "プロフィール",
      icon: <IconEsportsPersonSVG width={44.657} height={43.811} />,
      description: "プロフィールの作成/編集や公開",
    },
    {
      link: "/mypage/team-settings",
      title: "チーム",
      icon: <IconTeamOwnerSVG width={44.657} height={43.811} />,
      description: "チームの作成/編集や公開、所属メンバー確認",
    },
    {
      link: "/mypage/project",
      title: "プロジェクト",
      icon: <IconProjectSVG width={44.657} height={43.811} />,
      description:
        "プロジェクトの作成/編集や公開、応募済みのプロジェクトの確認",
    },
    {
      link: "/mypage/event-settings",
      title: "イベント",
      icon: <IconCalendarSVG width={44.657} height={43.811} />,
      description: "イベントの作成/編集や公開",
    },
    {
      link: "/mypage/topic-settings",
      title: "トピックス",
      icon: <IconTopicsSVG width={44.657} height={43.811} />,
      description: "トピックスの作成/編集や公開",
    },
    {
      link: "/mypage/facility-setting",
      title: "施設管理",
      icon: <IconFacilitySVG width={44.657} height={43.811} />,
      description: "施設の登録・編集・削除",
    },
    {
      link: "/mypage/chat",
      title: "チャット",
      icon: <IconChatSVG width={44.657} height={43.811} />,
      description: "他のユーザーとチャット",
    },
    {
      link: "/mypage/offer-history",
      title: "オファー履歴",
      icon: <FontAwesomeIcon icon={["fal", "handshake"]} />,
      description: "プロフィールへのオファー履歴",
    },
    {
      link: "/mypage/other-settings",
      title: "その他設定",
      icon: <IconGearSVG width={44.657} height={43.811} />,
      description: "パスワードの変更・ご連絡先の設定・アカウント削除など",
    },
  ];

  useMetaData({ title: "マイページ", description: "", imgUrl: "" });

  return (
    <React.Fragment>
      <PageTitle title={"MY PAGE"} />
      <div className={styles["c-mypage__layout"]}>
        <div className={styles["c-mypage__area-nav"]}>
          <MyPageSideBar
            settings={settings}
            logoutHandler={logoutHandler}
            showJikeiAdmin={
              roles?.includes("jikei_admin") || roles?.includes("jikei_manager")
                ? true
                : false
            }
            showAdmin={roles?.includes("admin") ? true : false}
          />
        </div>
        <div className={styles["c-mypage__area-main"]}>
          {/* Routes */}
          <Routes>
            <Route
              path="/"
              element={
                <MyPageTop settings={settings} logoutHandler={logoutHandler} />
              }
            />

            <Route
              path="settings"
              element={
                <RequireAuth component={DatabaseEntrySettingsContainer} />
              }
            />

            <Route
              path="settings/create"
              element={<RequireAuth component={CreateDatabaseEntryContainer} />}
            />

            <Route
              path="settings/:id"
              element={<RequireAuth component={EditDatabaseEntryContainer} />}
            />

            <Route
              path="team-settings"
              element={<RequireAuth component={TeamOwnerSettingsContainer} />}
            />

            <Route
              path="team-settings/create"
              element={<RequireAuth component={CreateTeamContainer} />}
            />

            <Route
              path="team-settings/:id"
              element={<RequireAuth component={EditTeamContainer} />}
            />

            <Route
              path="team_settings/member/:id"
              element={<RequireAuth component={TeamMembersContainer} />}
            />

            <Route
              path="project/settings/create"
              element={<RequireAuth component={CreateProjectContainer} />}
            />

            <Route
              path="project/settings/:id"
              element={<RequireAuth component={EditProjectContainer} />}
            />

            <Route
              path="project/*"
              element={<RequireAuth component={ArchiveMypageProject} />}
            />

            <Route
              path="project/settings"
              element={<RequireAuth component={ProjectSettingsContainer} />}
            />

            <Route
              path="event-settings"
              element={<RequireAuth component={EventSettingsContainer} />}
            />

            <Route
              path="event-settings/create"
              element={<RequireAuth component={CreateEventContainer} />}
            />

            <Route
              path="event-settings/:id"
              element={<RequireAuth component={EditEventContainer} />}
            />

            <Route
              path="topic-settings"
              element={<RequireAuth component={TopicSettingsContainer} />}
            />

            <Route
              path="topic-settings/create"
              element={<RequireAuth component={CreateTopicContainer} />}
            />

            <Route
              path="topic-settings/:id"
              element={<RequireAuth component={EditTopicContainer} />}
            />

            <Route path="/facility-setting">
              <Route
                index
                element={<RequireAuth component={FacilitySettingContainer} />}
              />
              <Route path=":id" element={<EditFacilityContainer />} />
              <Route path="create" element={<CreateFacilityContainer />} />
            </Route>

            <Route
              path="other-settings"
              element={<RequireAuth component={OtherSettingsPage} />}
            />

            <Route
              path="chat"
              element={<RequireAuth component={ChatListContainer} />}
            />

            <Route
              path="chat/:id"
              element={<RequireAuth component={ChatPageContainer} />}
            />

            <Route
              path="offer-history"
              element={<RequireAuth component={OfferHistoryContainer} />}
            />
          </Routes>
        </div>
      </div>
    </React.Fragment>
  );
};

export default MyPage;
